import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { safeLocal } from '../lib/safeStorage';
import { eventHub } from '../lib/tracking/event-hub';
import { isProductHiddenFromUI } from '../lib/productVisibility';

// في التطوير: استخدم البروكسي (نفس المنفذ) لتجنب CORS
const BACKEND_URL = import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com').replace(/\/$/, '');

export interface Product {
  id: string;
  title: string;
  price: number;
  image?: string;
  description?: string;
  category?: string; // ✅ إضافة category للتتبع
}

export interface CartItem extends Product {
  quantity: number;
}

export interface RemovedSoldOutItem {
  id: string;
  title: string;
}

// Helper: sync prices and remove sold-out items
const syncCartPricesAndStock = async (items: CartItem[]): Promise<{
  items: CartItem[];
  removedSoldOut: RemovedSoldOutItem[];
}> => {
  const removedSoldOut: RemovedSoldOutItem[] = [];
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${BACKEND_URL}/api/products?t=${timestamp}`, {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return { items, removedSoldOut };
    const products = await response.json();
    if (!Array.isArray(products)) return { items, removedSoldOut };
    if (products.length === 0) return { items, removedSoldOut };

    const keptItems: CartItem[] = [];
    for (const cartItem of items) {
      const currentProduct = products.find((p: any) => String(p.id) === String(cartItem.id));
      if (!currentProduct) {
        removedSoldOut.push({ id: cartItem.id, title: cartItem.title });
        continue;
      }
      if (currentProduct.soldOut === true || isProductHiddenFromUI(currentProduct)) {
        removedSoldOut.push({ id: cartItem.id, title: cartItem.title });
        continue;
      }
      const price =
        currentProduct.price !== cartItem.price ? currentProduct.price : cartItem.price;
      keptItems.push({ ...cartItem, price });
    }
    return { items: keptItems, removedSoldOut };
  } catch {
    return { items, removedSoldOut };
  }
};

interface CartState {
  items: CartItem[];
  count: number; // للحفاظ على التوافق مع المكونات الحالية
  total: number;
  
  addItem: (product: Product, quantity?: number) => Promise<void>;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  increment: () => void; // دالة قديمة، سنبقيها لعدم كسر الكود القديم إن وجد، لكن سنعدل سلوكها
  syncPrices: () => Promise<RemovedSoldOutItem[]>; // Sync prices + remove sold-out items
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      count: 0,
      total: 0,

      addItem: async (product, quantity = 1) => {
        set((state) => {
          const existingItem = state.items.find(item => item.id === product.id);
          let newItems;
          
          if (existingItem) {
            newItems = state.items.map(item => 
              item.id === product.id 
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            newItems = [...state.items, { ...product, quantity }];
          }

          // إعادة حساب الإجماليات
          const newCount = newItems.reduce((acc, item) => acc + item.quantity, 0);
          const newTotal = newItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);

          return { items: newItems, count: newCount, total: newTotal };
        });

        // Track AddToCart event
        try {
          await eventHub.addCart({
            value: product.price * quantity,
            currency: 'SAR',
            items: [{
              item_id: product.id.toString(), // ✅ تحويل إلى string لـ Meta CAPI (Item IDs)
              item_name: product.title,
              price: product.price,
              quantity: quantity,
              item_category: product.category || 'Sweets' // ✅ استخدام category من المنتج مع fallback
            }]
          });
        } catch (error) {
          // AddToCart event failed silently
        }
      },

      removeItem: (productId) => {
        set((state) => {
          const newItems = state.items.filter(item => item.id !== productId);
          const newCount = newItems.reduce((acc, item) => acc + item.quantity, 0);
          const newTotal = newItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
          return { items: newItems, count: newCount, total: newTotal };
        });
      },

      updateQuantity: (productId, quantity) => {
        set((state) => {
          if (quantity <= 0) {
            // إذا كانت الكمية 0 أو أقل، نحذف العنصر
            const newItems = state.items.filter(item => item.id !== productId);
            const newCount = newItems.reduce((acc, item) => acc + item.quantity, 0);
            const newTotal = newItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
            return { items: newItems, count: newCount, total: newTotal };
          }

          const newItems = state.items.map(item => 
            item.id === productId ? { ...item, quantity } : item
          );
          
          const newCount = newItems.reduce((acc, item) => acc + item.quantity, 0);
          const newTotal = newItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
          
          return { items: newItems, count: newCount, total: newTotal };
        });
      },

      clearCart: () => set({ items: [], count: 0, total: 0 }),

      // Sync prices with products.json and remove sold-out items
      syncPrices: async () => {
        const state = get();
        if (state.items.length === 0) return [];
        const { items: syncedItems, removedSoldOut } = await syncCartPricesAndStock(state.items);
        const newCount = syncedItems.reduce((acc, item) => acc + item.quantity, 0);
        const newTotal = syncedItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
        set({ items: syncedItems, count: newCount, total: newTotal });
        return removedSoldOut;
      },

      // دالة legacy لدعم الكود القديم الذي يستدعي increment
      // سنفترض أنها تضيف منتج وهمي أو تزيد العداد فقط إذا لم نكن نريد ربطها بمنتج حقيقي
      // لكن الأفضل توجيه المطور لاستخدام addItem
      increment: () => set((state) => ({ count: state.count + 1 })),
    }),
    {
      name: 'cart-storage', // اسم التخزين في localStorage
      storage: createJSONStorage(() => safeLocal),
    }
  )
);
